import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, InputAdornment, LinearProgress,
  Paper, List, ListItemButton, ListItemText, Checkbox, Chip, ButtonBase, Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Tag as TagIconIx } from "iconsax-react";
import { getAuthToken } from "../../../../../utils/getAuthToken";
import { SERVER_DOMAIN, COUNTRY_LIST, EMPTY_CONTACT_FILTER, CONTACT_FILTER_OPTIONS } from "../constants";
import { inboxInputSx, inboxMenuItemSx } from "./LabeledField";

// Selected = solid primary.main fill (Rahim, 2026-09-04: "better to use
// green filled for the selected button" — the soft-tint pass before this
// was rejected too; green fill explicitly asked for here, an intentional
// exception to this page's usual "never a green button" rule for this one
// selected-state specifically).
const segmentedOptionSx = (active) => ({
  borderRadius: "8px",
  border: "1px solid",
  borderColor: (theme) => (active ? theme.palette.primary.main : theme.palette.divider),
  bgcolor: (theme) => (active ? theme.palette.primary.main : theme.palette.background.default),
  color: (theme) => (active ? theme.palette.primary.contrastText : theme.palette.text.secondary),
  px: 1.5, py: 0.75,
  fontSize: "0.8125rem",
  fontWeight: active ? 600 : 500,
  transition: "background-color 0.15s, border-color 0.15s",
  "&:hover": {
    bgcolor: (theme) => (active ? theme.palette.primary.dark : theme.palette.action.hover),
  },
});

// Labels rebuilt as small squared buttons instead of a rounded pill/Chip
// (Rahim, 2026-09-04: "for labels use this type button and dont make it a
// pill use square type one with less corner radius" — pointing at
// InboxAll.jsx's own "Filter by Labels" checklist rows, which use a tag
// icon + text, not a filled chip). Same TagIconIx Inbox uses there. Green
// fill when selected, matching segmentedOptionSx's own selected state
// above for one consistent "selected" language on this whole section.
const labelButtonSx = (selected) => ({
  borderRadius: "4px",
  border: "1px solid",
  borderColor: (theme) => (selected ? theme.palette.primary.main : theme.palette.divider),
  bgcolor: (theme) => (selected ? theme.palette.primary.main : theme.palette.background.default),
  color: (theme) => (selected ? theme.palette.primary.contrastText : theme.palette.text.secondary),
  display: "flex", alignItems: "center", gap: 0.625,
  px: 1.25, py: 0.625,
  fontSize: "0.8125rem",
  fontWeight: selected ? 600 : 500,
  maxWidth: 200,
  transition: "background-color 0.15s, border-color 0.15s",
  "&:hover": {
    bgcolor: (theme) => (selected ? theme.palette.primary.dark : theme.palette.action.hover),
  },
});

// Neutral pill for the other selected-value chip lists (specific contacts,
// country codes) — 8px radius/divider border/text.primary instead of MUI's
// flat default Chip look. No per-item color coding here (unlike labels,
// which get their own dedicated accent above) since there's no natural
// "type" to color these by.
const neutralPillSx = {
  borderRadius: "8px",
  bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
  border: "1px solid", borderColor: "divider",
  color: "text.primary",
  fontWeight: 500,
  fontSize: "0.8125rem",
  "& .MuiChip-deleteIcon": { fontSize: "14px", color: "text.secondary", "&:hover": { color: "text.primary" } },
};

export default function ContactFilterSection({ contactFilter, setForm }) {
  const cf = { ...EMPTY_CONTACT_FILTER, ...(contactFilter || {}) };

  const setFilter = (patch) =>
    setForm((prev) => ({
      ...prev,
      contactFilter: { ...(prev.contactFilter || EMPTY_CONTACT_FILTER), ...patch },
    }));

  const [contactSearch, setContactSearch] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [contactSearching, setContactSearching] = useState(false);

  useEffect(() => {
    if (cf.filterType !== "specific_contacts" || !contactSearch.trim()) {
      setContactResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setContactSearching(true);
      try {
        const token = await getAuthToken();
        const params = new URLSearchParams({ search: contactSearch, limit: "20" });
        const res = await fetch(`${SERVER_DOMAIN}/api/contacts/get?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setContactResults(Array.isArray(data?.contacts) ? data.contacts : []);
      } catch {} finally { setContactSearching(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [contactSearch, cf.filterType]);

  const toggleContact = (c) => {
    const existing = cf.specificContacts || [];
    const alreadyIn = existing.some((x) => x.id === String(c._id));
    setFilter({
      specificContacts: alreadyIn
        ? existing.filter((x) => x.id !== String(c._id))
        : [...existing, { id: String(c._id), name: c.name || "", phone: c.phoneNumberID || "" }],
    });
  };

  const [countrySearch, setCountrySearch] = useState("");

  const toggleCountry = (code) => {
    const codes = cf.countryCodes || [];
    setFilter({ countryCodes: codes.includes(code) ? codes.filter((c) => c !== code) : [...codes, code] });
  };

  const filteredCountries = countrySearch.trim()
    ? COUNTRY_LIST.filter(
        (c) =>
          c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
          c.dialCode.includes(countrySearch)
      )
    : COUNTRY_LIST;

  const [availableLabels, setAvailableLabels] = useState([]);
  // labelsFetched (not just availableLabels.length>0) — the old guard
  // couldn't tell "haven't fetched yet" from "fetched, workspace genuinely
  // has zero labels", so a real empty workspace showed "Loading labels…"
  // forever (Rahim, 2026-09-04 audit, found while re-verifying the
  // skeleton-coverage claims). labelsLoading drives the new skeleton below.
  const [labelsFetched, setLabelsFetched] = useState(false);
  const [labelsLoading, setLabelsLoading] = useState(false);

  useEffect(() => {
    if (cf.filterType !== "label" || labelsFetched) return;
    setLabelsLoading(true);
    getAuthToken().then((token) => {
      fetch(`${SERVER_DOMAIN}/api/label/get`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((data) => setAvailableLabels(Array.isArray(data?.labels) ? data.labels : []))
        .catch(() => {})
        .finally(() => { setLabelsFetched(true); setLabelsLoading(false); });
    });
  }, [cf.filterType, labelsFetched]);

  const toggleLabel = (lbl) => {
    const labels = cf.labels || [];
    setFilter({ labels: labels.includes(lbl) ? labels.filter((l) => l !== lbl) : [...labels, lbl] });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Segmented filter-type row — was a plain MUI ToggleButtonGroup
          (default blue-tinted selected state); rebuilt as plain
          ButtonBase rows sharing segmentedOptionSx so the selected state
          matches this page's own dark-fill convention instead. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {CONTACT_FILTER_OPTIONS.map((opt) => {
          const active = cf.filterType === opt.value;
          return (
            <ButtonBase
              key={opt.value}
              onClick={() => setFilter({ filterType: opt.value })}
              sx={segmentedOptionSx(active)}
            >
              {opt.label}
            </ButtonBase>
          );
        })}
      </Box>

      {cf.filterType === "specific_contacts" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {(cf.specificContacts || []).length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {(cf.specificContacts || []).map((c) => (
                <Chip
                  key={c.id} size="small"
                  label={c.name ? `${c.name}${c.phone ? ` (${c.phone})` : ""}` : c.id}
                  onDelete={() =>
                    setFilter({ specificContacts: (cf.specificContacts || []).filter((x) => x.id !== c.id) })
                  }
                  sx={neutralPillSx}
                />
              ))}
            </Box>
          )}
          <TextField
            size="small" placeholder="Search contacts by name or phone…"
            value={contactSearch} onChange={(e) => setContactSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.secondary" }} /></InputAdornment> }}
            sx={inboxInputSx}
          />
          {contactSearching && <LinearProgress sx={{ borderRadius: 1 }} />}
          {contactResults.length > 0 && (
            <Paper variant="outlined" sx={{ maxHeight: 200, overflowY: "auto", borderRadius: "8px" }}>
              <List dense disablePadding sx={{ p: 0.5 }}>
                {contactResults.map((c) => {
                  const selected = (cf.specificContacts || []).some((x) => x.id === String(c._id));
                  return (
                    <ListItemButton key={c._id} selected={selected} onClick={() => toggleContact(c)} dense sx={inboxMenuItemSx}>
                      <Checkbox size="small" edge="start" checked={selected} disableRipple sx={{ p: 0, mr: 1 }} />
                      <ListItemText
                        primary={c.name || "Unknown"} secondary={c.phoneNumberID}
                        primaryTypographyProps={{ sx: { fontSize: "0.8125rem", color: "text.primary" } }}
                        secondaryTypographyProps={{ sx: { fontSize: "0.6875rem", color: "text.secondary" } }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Paper>
          )}
        </Box>
      )}

      {cf.filterType === "country_code" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {(cf.countryCodes || []).length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {(cf.countryCodes || []).map((code) => {
                const country = COUNTRY_LIST.find((c) => c.code === code);
                return (
                  <Chip
                    key={code} size="small"
                    label={country ? `${country.name} (${country.dialCode})` : code}
                    onDelete={() => toggleCountry(code)}
                    sx={neutralPillSx}
                  />
                );
              })}
            </Box>
          )}
          <TextField
            size="small" placeholder="Search country…" value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.secondary" }} /></InputAdornment> }}
            sx={inboxInputSx}
          />
          {filteredCountries.length > 0 && (
            <Paper variant="outlined" sx={{ maxHeight: 200, overflowY: "auto", borderRadius: "8px" }}>
              <List dense disablePadding sx={{ p: 0.5 }}>
                {filteredCountries.slice(0, 80).map((c) => {
                  const selected = (cf.countryCodes || []).includes(c.code);
                  return (
                    <ListItemButton key={c.code} selected={selected} onClick={() => toggleCountry(c.code)} dense sx={inboxMenuItemSx}>
                      <Checkbox size="small" edge="start" checked={selected} disableRipple sx={{ p: 0, mr: 1 }} />
                      <ListItemText
                        primary={`${c.name} (${c.dialCode})`}
                        primaryTypographyProps={{ sx: { fontSize: "0.8125rem", color: "text.primary" } }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Paper>
          )}
        </Box>
      )}

      {cf.filterType === "label" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {labelsLoading && (
            // Skeleton pills matching labelButtonSx's own shape (4px
            // radius, not a rounded pill) instead of plain "Loading
            // labels…" text (Rahim, 2026-09-04 audit).
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {[64, 84, 52].map((w, i) => (
                <Skeleton key={i} variant="rounded" width={w} height={30} sx={{ borderRadius: "4px" }} />
              ))}
            </Box>
          )}
          {!labelsLoading && labelsFetched && availableLabels.length === 0 && (
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>No labels found.</Typography>
          )}
          {availableLabels.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {availableLabels.map((lbl) => {
                const selected = (cf.labels || []).includes(lbl);
                return (
                  <ButtonBase key={lbl} onClick={() => toggleLabel(lbl)} sx={labelButtonSx(selected)}>
                    <Box sx={{ width: 14, height: 14, display: "flex", flexShrink: 0 }}>
                      <TagIconIx size="100%" color="currentColor" variant="Linear" />
                    </Box>
                    <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {lbl}
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
